import {
  Color,
  FramebufferTexture,
  Material,
  Mesh,
  PerspectiveCamera,
  RGBAFormat,
  Scene,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass";
import { TexturePass } from "three/examples/jsm/postprocessing/TexturePass";
import { UnrealBloomPass } from "../AlphaUnrealBloom";
import { BloomProps } from "../CanvasComponent";
import { GammaCorrectionShader } from "../GammaCorrectionShader";
import { onBeforeCompileBloom } from "../utils/bloomHelper";

type propsBloomPipeline = {
  xr8: any;
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  bloomProps: BloomProps;
};

export const bloomPipeline = ({
  scene,
  camera,
  renderer,
  bloomProps,
}: propsBloomPipeline) => {
  const combineShaderObject = {
    shaderId: "combineShader",
    uniforms: {
      cameraTexture: { value: undefined },
      tDiffuse: { value: null }, // Scene contents
      bloomTexture: { value: null }, // Bloom effect
      //   u_resolutionRatio: { value: new Vector2(1, 1) },
      useAdditiveBlend: { value: false },
    },
    vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }`,
    fragmentShader: /* glsl */ `
        uniform sampler2D cameraTexture;
        uniform sampler2D tDiffuse; // Scene contents 
        uniform sampler2D bloomTexture;
        uniform vec2 u_resolutionRatio;
        uniform bool useAdditiveBlend;
        
        varying vec2 vUv;
        
        vec4 normalBlend(vec4 x, vec4 y, float opacity) {
        return y * opacity + x * (1.0 - opacity);
        }
        
        void main(void) {
          vec4 cameraColor = texture2D( cameraTexture, vUv);
          vec4 sceneColor = texture2D( tDiffuse, vUv);
          vec4 bloomColor = texture2D( bloomTexture, vUv);
        
          gl_FragColor = normalBlend(cameraColor, sceneColor, sceneColor.a);
          gl_FragColor += bloomColor;
          // gl_FragColor += bloomColor;
          // gl_FragColor = vec4(length(cameraColor.rgb), length(bloomColor.rgb), length(sceneColor.rgb), 1.); // Shows camera in red and scene in blue
        }
        `,
  };

  const combineShaderFrag = combineShaderObject.fragmentShader;
  const combineShaderVert = combineShaderObject.vertexShader;
  const combineShader = {
    uniforms: {
      cameraTexture: { value: undefined },
      tDiffuse: { value: null },
      useAdditiveBlend: { value: false },
      bloomTexture: { value: undefined },
    },
    fragmentShader: combineShaderFrag,
    vertexShader: combineShaderVert,
  };
  let cameraTexture: FramebufferTexture;
  let sceneTarget: WebGLRenderTarget;
  let notBloomedTarget: WebGLRenderTarget;
  let copyPass: any;
  let combinePass: ShaderPass;
  const cameraTextureCopyPosition = new Vector2(0, 0);
  let bloomPass: UnrealBloomPass;
  let bloomComposer: EffectComposer;
  let composer: EffectComposer;

  let isSetup = false;

  // const canvasChange = (canvasWidth: number, canvasHeight: number) => {
  //   console.log("canvasChange", canvasWidth, canvasHeight, isSetup);
  //   if (!isSetup) {
  //     return;
  //   }

  //   cameraTexture = new FramebufferTexture(
  //     canvasWidth,
  //     canvasHeight,
  //     RGBAFormat
  //   );
  //   renderer.setSize(canvasWidth, canvasHeight);
  //   const pixelRatio = MathUtils.clamp(window.devicePixelRatio, 1, 2);
  //   renderer.pixelRatio = pixelRatio;

  //   // Update render pass sizes
  //   bloomComposer.setSize(canvasWidth * pixelRatio, canvasHeight * pixelRatio);
  //   bloomComposer.passes.forEach((pass: any) => {
  //     if (pass.setSize) {
  //       pass.setSize(canvasWidth * pixelRatio, canvasHeight * pixelRatio);
  //     }
  //   });
  //   composer.setSize(canvasWidth * pixelRatio, canvasHeight * pixelRatio);
  //   composer.passes.forEach((pass: any) => {
  //     if (pass.setSize) {
  //       pass.setSize(canvasWidth * pixelRatio, canvasHeight * pixelRatio);
  //     }
  //   });
  //   if (bloomPass && combinePass && sceneTarget && copyPass) {
  //     combinePass.uniforms.cameraTexture = { value: cameraTexture };
  //     combinePass.uniforms.bloomTexture = {
  //       value: bloomPass.renderTargetsHorizontal[0],
  //     };
  //     sceneTarget.setSize(canvasWidth * pixelRatio, canvasHeight * pixelRatio);
  //     notBloomedTarget.setSize(
  //       canvasWidth * pixelRatio,
  //       canvasHeight * pixelRatio
  //     );
  //     copyPass.uniforms.tDiffuse = { value: sceneTarget.texture };
  //   }
  // };

  const setupBloom = (canvasWidth: number, canvasHeight: number) => {
    if (isSetup) {
      return;
    }
    isSetup = true;

    renderer.debug.checkShaderErrors = false; // speeds up loading new materials
    renderer.autoClear = false;
    renderer.autoClearDepth = false;
    renderer.setClearColor(0x000000, 0);
    renderer.toneMappingExposure = bloomProps.exposure ** 4; // same as Math.pow(bloomProps.exposure, 4)

    sceneTarget = new WebGLRenderTarget(canvasWidth, canvasHeight, {
      generateMipmaps: false,
    });
    notBloomedTarget = new WebGLRenderTarget(canvasWidth, canvasHeight, {
      generateMipmaps: false,
    });
    // Bloom Composer
    bloomComposer = new EffectComposer(renderer);
    bloomComposer.renderToScreen = false;
    // Copy scene into bloom
    copyPass = new TexturePass(sceneTarget.texture);
    bloomComposer.addPass(copyPass);
    // Bloom Pass
    bloomPass = new UnrealBloomPass(
      new Vector2(canvasWidth, canvasHeight),
      bloomProps.strenght,
      bloomProps.radius,
      bloomProps.threshold
    );
    bloomPass.renderToScreen = false;
    bloomPass.clearColor = new Color(0x000000);
    bloomComposer.addPass(bloomPass);
    // Final composer
    composer = new EffectComposer(renderer);
    const notBloomedPass = new TexturePass(notBloomedTarget.texture);
    composer.addPass(notBloomedPass);
    // Gamma Correction Shader Pass
    const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
    composer.addPass(gammaCorrectionPass);
    // Combine scene and camerafeed pass
    combinePass = new ShaderPass(combineShader);
    combinePass.clear = false;
    combinePass.renderToScreen = true;
    composer.addPass(combinePass);

    cameraTexture = new FramebufferTexture(
      canvasWidth,
      canvasHeight,
      RGBAFormat
    );

    combinePass.uniforms.cameraTexture = { value: cameraTexture };
    combinePass.uniforms.bloomTexture = {
      value: bloomPass.renderTargetsHorizontal[0].texture,
    };
    copyPass.uniforms.tDiffuse = { value: sceneTarget.texture };
  };
  let trackingDone = false;

  return {
    name: "bloomPipeline",
    onException: (err: any) => console.error(err),
    onCanvasSizeChange: ({
      canvasWidth,
      canvasHeight,
    }: {
      canvasWidth: number;
      canvasHeight: number;
    }) => {
      // canvasChange(canvasWidth, canvasHeight);
    },
    onStart: ({
      canvasWidth,
      canvasHeight,
    }: {
      canvasWidth: number;
      canvasHeight: number;
      canvas: any;
      GLctx: any;
    }) => {
      setupBloom(canvasWidth, canvasHeight);
    },
    // onCameraStatusChange: ({ status }: any) => {
    //   console.log("has video", status);
    // },
    onUpdate: ({ processCpuResult, fps }: any) => {
      if (
        !trackingDone &&
        (processCpuResult?.reality?.trackingStatus === "NORMAL" ||
          processCpuResult?.reality?.trackingStatus === "LIMITED")
      ) {
        trackingDone = true;
      }
      if (!trackingDone && processCpuResult.facecontroller) {
        trackingDone = true;
      }
    },
    onRender: () => {
      if (!renderer || !trackingDone) {
        return;
      }
      if (scene) {
        // make sure that models which haven't set the onBeforeCompile are not affected by bloom
        // TB: I'm not happy with this as it traverses the scene on every render
        // but it also allows for dynamically adding objects to the scene that don't bloom per default
        // and enable bloom for those that need to have it with a useData.bloom setting.
        // If it proofs to kill the performance we could just add it as a one time call once the scene is setup
        // and for dynamic adding of objects we need to find another way.
        scene.traverse((obj) => {
          if ((obj as Mesh).isMesh) {
            if (!obj.userData.bloom) {
              ((obj as Mesh).material as Material).onBeforeCompile =
                onBeforeCompileBloom;
            } else {
              ((obj as Mesh).material as Material).onBeforeCompile = () => {};
            }
          }
        });

        if (cameraTexture) {
          renderer.copyFramebufferToTexture(
            cameraTextureCopyPosition,
            cameraTexture
          );
        }

        if (sceneTarget) {
          renderer.setRenderTarget(sceneTarget);
        }
        renderer.clear();
        renderer.clearDepth();
        // Make other materials (non bloom) render black. See GltfComponent search: onBeforeCompile
        window.RPR.globalBloom.value = 1;
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);

        if (notBloomedTarget) {
          renderer.setRenderTarget(notBloomedTarget);
        }
        renderer.clear();
        renderer.clearDepth();
        // Render everything with colours
        window.RPR.globalBloom.value = 0;
        renderer.render(scene, camera);

        renderer.setRenderTarget(null);

        bloomComposer.render();
        composer.render();
      }
    },
  };
};
